import React from "react";
import { Fish, ArrowRight, MapPin, Ban, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import PageHero from "../components/PageHero";
import GewaesserKarte from "../components/GewaesserKarte";
import { images, waters, waterList, club, getWaterStatus, getSortedRestrictions, getActiveWaters } from "../mock/mock";

export default function Gewaesser() {
  const activeWaters = getActiveWaters();
  const withCoords = activeWaters.filter((w) => w.coords).length;
  return (
    <div>
      <PageHero
        eyebrow="Unsere Reviere"
        title="Gewässer"
        subtitle="Vielfältige Kanäle, Flüsse und Weiher rund um Fürth – naturnah und gut gepflegt."
        image={images.woernitz}
      />

      {/* INTERAKTIVE KARTE */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-10">
            <span className="inline-block text-accent text-sm font-600 uppercase tracking-[0.25em] mb-3">
              Auf der Karte
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-600 text-foreground leading-tight">
              Unsere Gewässer entdecken
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-300">
              Klicke auf einen Marker, um Details zum jeweiligen Gewässer zu
              sehen. Aktuell sind {withCoords} von {activeWaters.length} Standorten
              hinterlegt – weitere folgen laufend.
            </p>
          </div>
          <GewaesserKarte />
        </div>
      </section>

      <section className="pb-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-accent text-sm font-600 uppercase tracking-[0.25em] mb-3">
              Im Detail
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-600 text-foreground leading-tight">
              Unsere Gewässer im Porträt
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-300">
              Ein Einblick in einige unserer schönsten Reviere – mit echten
              Aufnahmen unserer Mitglieder.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {waters.map((w) => (
              <div
                key={w.id}
                className="group bg-card rounded-2xl overflow-hidden border border-border shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={w.image}
                    alt={w.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <span className="absolute top-4 left-4 text-[11px] font-600 uppercase tracking-wider bg-background/90 backdrop-blur-sm text-primary px-3 py-1 rounded-full">
                    {w.type}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-serif text-2xl font-600 text-foreground">
                    {w.name}
                  </h3>
                  <p className="mt-2 text-muted-foreground text-sm leading-relaxed flex-1">
                    {w.description}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {w.fish.map((f) => (
                      <span
                        key={f}
                        className="inline-flex items-center gap-1.5 text-xs bg-secondary text-secondary-foreground px-2.5 py-1 rounded-full"
                      >
                        <Fish className="h-3 w-3 text-primary" />
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VOLLSTÄNDIGES GEWÄSSERVERZEICHNIS */}
      <section className="py-24 bg-secondary/40 border-t border-border">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-accent text-sm font-600 uppercase tracking-[0.25em] mb-3">
              Alle Reviere
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-600 text-foreground leading-tight">
              Unser Gewässerverzeichnis
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-300">
              Über 30 Vereins- und Verbandsgewässer in ganz Franken und der
              Oberpfalz – laut Erlaubnisschein 2026.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {activeWaters.map((w) => {
              const { locked, notices } = getWaterStatus(w);
              const warnNotice = notices.find((n) => n.timing.state === "upcoming");
              const activeRestriction = notices.find(
                (n) => n.timing.state === "active" && n.type !== "closure"
              );
              return (
              <div
                key={w.nr}
                className="group bg-card rounded-2xl overflow-hidden border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="relative h-36 overflow-hidden">
                  {w.img ? (
                    <img
                      src={w.img}
                      alt={w.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/15 to-secondary flex items-center justify-center">
                      <Fish className="h-10 w-10 text-primary/30" />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 font-serif text-lg font-700 text-white drop-shadow-md bg-primary/70 backdrop-blur-sm h-9 w-9 flex items-center justify-center rounded-full">
                    {w.nr}
                  </span>
                  <span className="absolute top-3 right-3 text-[10px] font-600 uppercase tracking-wider bg-background/90 backdrop-blur-sm text-primary px-2.5 py-1 rounded-full">
                    {w.type}
                  </span>
                  {locked ? (
                    <span className="absolute bottom-3 left-3 flex items-center gap-1 text-[10px] font-700 uppercase tracking-wider bg-destructive text-destructive-foreground px-2.5 py-1 rounded-full shadow">
                      <Ban className="h-3 w-3" /> Gesperrt
                    </span>
                  ) : warnNotice ? (
                    <span className="absolute bottom-3 left-3 flex items-center gap-1 text-[10px] font-700 uppercase tracking-wider bg-amber-500 text-white px-2.5 py-1 rounded-full shadow">
                      <AlertTriangle className="h-3 w-3" /> Ab {warnNotice.timing.startLabel}
                    </span>
                  ) : activeRestriction ? (
                    <span className="absolute bottom-3 left-3 flex items-center gap-1 text-[10px] font-700 uppercase tracking-wider bg-amber-500 text-white px-2.5 py-1 rounded-full shadow">
                      <AlertTriangle className="h-3 w-3" /> Einschränkung
                    </span>
                  ) : null}
                </div>
                <div className="p-4">
                  <h3 className="font-600 text-foreground leading-tight">{w.name}</h3>
                  {notices.map((n) => {
                    const upcoming = n.timing.state === "upcoming";
                    const isClosure = n.type === "closure";
                    const cls = isClosure && !upcoming ? "text-destructive" : "text-amber-600";
                    const text = upcoming
                      ? `Achtung: ${n.title} ab ${n.timing.startLabel} (in ${n.timing.daysUntil} ${n.timing.daysUntil === 1 ? "Tag" : "Tagen"})`
                      : isClosure
                      ? `Gesperrt – ${n.title} (${n.rangeLabel})`
                      : `${n.title}${n.rangeLabel !== "dauerhaft" ? ` (${n.rangeLabel})` : ""}`;
                    return (
                      <p key={n.id} className={`text-xs font-600 mt-1 flex items-start gap-1.5 ${cls}`}>
                        {isClosure && !upcoming ? (
                          <Ban className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        ) : (
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        )}
                        {text}
                      </p>
                    );
                  })}
                  {w.note && (
                    <p className="text-sm text-muted-foreground mt-1 leading-snug flex items-start gap-1.5">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-accent" />
                      {w.note}
                    </p>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SPERREN & EINSCHRÄNKUNGEN – automatisch nach Datum sortiert */}
      <section className="py-24 bg-background border-t border-border">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-accent text-sm font-600 uppercase tracking-[0.25em] mb-3">
              Immer aktuell
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-600 text-foreground leading-tight">
              Sperren & Fangeinschränkungen
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-300">
              Alle offiziellen Regelungen laut Fischereiordnung – automatisch nach
              Datum sortiert. Der Status aktualisiert sich täglich von selbst.
            </p>
          </div>
          <div className="space-y-4">
            {getSortedRestrictions().map((r) => {
              const active = r.timing.state === "active";
              const upcoming = r.timing.state === "upcoming";
              const isClosure = r.type === "closure";
              const scopeLabel =
                r.scope === "all" ? "Alle Vereinsgewässer" : r.scope.join(", ");
              return (
                <div
                  key={r.id}
                  data-testid={`restriction-${r.id}`}
                  className={`bg-card rounded-2xl border p-5 md:p-6 flex flex-col sm:flex-row sm:items-start gap-4 ${
                    active && isClosure
                      ? "border-destructive/40"
                      : active || upcoming
                      ? "border-amber-400/50"
                      : "border-border"
                  }`}
                >
                  <div
                    className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${
                      isClosure ? "bg-destructive/10 text-destructive" : "bg-amber-100 text-amber-600"
                    }`}
                  >
                    {isClosure ? <Ban className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-serif text-xl font-600 text-foreground">{r.title}</h3>
                      {active && (
                        <span className={`text-[10px] font-700 uppercase tracking-wider px-2.5 py-1 rounded-full ${isClosure ? "bg-destructive text-destructive-foreground" : "bg-amber-500 text-white"}`}>
                          {isClosure ? "Aktiv gesperrt" : "Aktiv"}
                        </span>
                      )}
                      {upcoming && (
                        <span className="text-[10px] font-700 uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-500 text-white">
                          Ab {r.timing.startLabel} · in {r.timing.daysUntil} {r.timing.daysUntil === 1 ? "Tag" : "Tagen"}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-600 text-primary">
                      {scopeLabel} · {r.rangeLabel}
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                      {r.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary/50 border-t border-border">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-600 text-foreground">
            Bereit für den nächsten Angeltag?
          </h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Sichert euch eure Tageskarte – online oder an unseren Verkaufsstellen.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={club.hejfish} target="_blank" rel="noopener noreferrer">
              <Button className="bg-accent hover:bg-accent/90 text-accent-foreground font-600">
                Online kaufen
              </Button>
            </a>
            <Link to="/gastkarten">
              <Button variant="outline" className="border-primary/30 text-primary hover:bg-secondary font-500 gap-2">
                Verkaufsstellen <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
